import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { X, Star, Sparkles, Crown, Gift, Zap } from 'lucide-react';
import { Button } from './ui/button';

const ExitIntentPopup = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [hasShown, setHasShown] = useState(false);
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const { user } = useAuth();

  // Check if user is already premium
  const isPremium = user?.subscription_tier && user.subscription_tier !== 'free';

  useEffect(() => {
    // Don't show for premium users or if already shown
    if (isPremium || hasShown) return;

    // Check if already shown in this session
    const alreadyShown = sessionStorage.getItem('exitPopupShown');
    if (alreadyShown) return;

    const handleMouseLeave = (e) => {
      // Detect when mouse leaves the viewport from the top (exit intent)
      if (e.clientY <= 0 && !hasShown) {
        setShowPopup(true);
        setHasShown(true);
        sessionStorage.setItem('exitPopupShown', 'true');
      }
    };

    // Also trigger on back button or tab close attempt
    const handleBeforeUnload = (e) => {
      if (!hasShown && !isPremium) {
        setShowPopup(true);
        setHasShown(true);
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    
    // Mobile: trigger after 30 seconds of inactivity or scroll up
    let inactivityTimer;
    const resetTimer = () => {
      clearTimeout(inactivityTimer);
      inactivityTimer = setTimeout(() => {
        if (!hasShown && !isPremium) {
          const alreadyShown = sessionStorage.getItem('exitPopupShown');
          if (!alreadyShown) {
            setShowPopup(true);
            setHasShown(true);
            sessionStorage.setItem('exitPopupShown', 'true');
          }
        }
      }, 60000); // Show after 60 seconds of no interaction
    };

    document.addEventListener('touchstart', resetTimer);
    document.addEventListener('scroll', resetTimer);
    resetTimer();

    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('touchstart', resetTimer);
      document.removeEventListener('scroll', resetTimer);
      clearTimeout(inactivityTimer);
    };
  }, [hasShown, isPremium]);

  const handleClose = () => {
    setShowPopup(false);
  };

  const handleSubscribe = () => {
    setShowPopup(false);
    navigate('/subscription');
  };

  // Don't render for premium users
  if (isPremium) return null;

  const content = {
    hi: {
      title: 'रुकिए! एक खास ऑफर',
      subtitle: 'अपनी ज्योतिष यात्रा अधूरी न छोड़ें',
      discount: '20% छूट',
      features: [
        'असीमित कुंडली जनरेशन',
        'दैनिक व्यक्तिगत भविष्यवाणियां',
        'प्रीमियम कुंडली मिलान',
        'विस्तृत हस्तरेखा विश्लेषण'
      ],
      cta: 'अभी सब्सक्राइब करें',
      later: 'बाद में',
      limitedTime: 'सीमित समय का ऑफर',
      startsAt: 'केवल ₹99/वर्ष से शुरू'
    },
    en: {
      title: 'Wait! Special Offer',
      subtitle: "Don't leave your astrology journey incomplete",
      discount: '20% OFF',
      features: [
        'Unlimited Kundli Generation',
        'Daily Personalized Predictions',
        'Premium Kundli Matching',
        'Detailed Palm Analysis'
      ],
      cta: 'Subscribe Now',
      later: 'Maybe Later',
      limitedTime: 'Limited Time Offer',
      startsAt: 'Starting at just ₹99/year'
    }
  };

  const t = content[language] || content.en;

  return (
    <AnimatePresence>
      {showPopup && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={handleClose}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 50 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
            className={`relative max-w-md w-full rounded-3xl overflow-hidden shadow-2xl ${
              isDark 
                ? 'bg-gradient-to-br from-white via-[#FFF9EF] to-[#FCE9D9]' 
                : 'bg-gradient-to-br from-white via-[#FFF9EF] to-amber-50'
            }`}
          >
            {/* Decorative elements */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
              <div className="absolute -top-20 -right-20 w-40 h-40 bg-gradient-to-br from-[#C84B32]/30 to-[#F5B942]/30 rounded-full blur-3xl" />
              <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-gradient-to-br from-[#E85D43]/20 to-[#F5B942]/30 rounded-full blur-3xl" />
            </div>

            {/* Close button */}
            <button
              onClick={handleClose}
              className={`absolute top-4 right-4 p-2 rounded-full transition-colors z-10 ${
                isDark ? 'hover:bg-[#FCE9D9] text-white/60' : 'hover:bg-[#FCE9D9]/60 text-[#6B6560]'
              }`}
            >
              <X className="w-5 h-5" />
            </button>

            {/* Content */}
            <div className="relative p-6 pt-8">
              {/* Badge */}
              <div className="flex justify-center mb-4">
                <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-red-500 to-orange-500 text-white text-sm font-bold animate-pulse">
                  <Zap className="w-4 h-4" />
                  {t.limitedTime}
                </span>
              </div>

              {/* Icon */}
              <div className="flex justify-center mb-4">
                <div className="relative">
                  <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#C84B32] to-[#F5B942] flex items-center justify-center shadow-lg shadow-[#C84B32]/30">
                    <Gift className="w-10 h-10 text-white" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-red-500 flex items-center justify-center text-white text-xs font-bold">
                    {t.discount.split(' ')[0]}
                  </div>
                </div>
              </div>

              {/* Title */}
              <h2 className={`text-2xl font-bold text-center mb-2 font-['Fraunces'] ${
                isDark ? 'text-white' : 'text-[#282622]'
              }`}>
                {t.title}
              </h2>
              <p className={`text-center mb-6 ${isDark ? 'text-[#6B6560]' : 'text-[#6B6560]'}`}>
                {t.subtitle}
              </p>

              {/* Features */}
              <div className="space-y-3 mb-6">
                {t.features.map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className={`flex items-center gap-3 p-3 rounded-xl ${
                      isDark ? 'bg-[#FCE9D9]/40' : 'bg-[#FCE9D9]/60'
                    }`}
                  >
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#C84B32] to-[#F5B942] flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-4 h-4 text-white" />
                    </div>
                    <span className={`text-sm ${isDark ? 'text-white' : 'text-[#282622]'}`}>
                      {feature}
                    </span>
                  </motion.div>
                ))}
              </div>

              {/* Price */}
              <p className={`text-center mb-4 text-sm ${isDark ? 'text-[#C84B32]' : 'text-[#C84B32]'} font-medium`}>
                <Crown className="w-4 h-4 inline mr-1" />
                {t.startsAt}
              </p>

              {/* CTA Buttons */}
              <div className="space-y-3">
                <Button
                  onClick={handleSubscribe}
                  className="w-full py-4 bg-gradient-to-r from-[#C84B32] via-amber-500 to-[#F5B942] hover:from-amber-500 hover:via-[#F5B942] hover:to-amber-500 text-[#282622] font-bold text-lg rounded-xl shadow-lg shadow-amber-500/30 transition-all hover:scale-[1.02]"
                >
                  <Star className="w-5 h-5 mr-2" />
                  {t.cta}
                </Button>
                <button
                  onClick={handleClose}
                  className={`w-full py-3 text-sm transition-colors ${
                    isDark ? 'text-[#6B6560] hover:text-white' : 'text-[#6B6560] hover:text-[#282622]'
                  }`}
                >
                  {t.later}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ExitIntentPopup;
